/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { t } from 'i18next';
import { Input } from 'core/components//ui/Input';
import { Button } from 'core/components/ui/Button';
import { AimOutlined } from 'core/components/ui/Design';
import { InputNumber } from 'core/components/ui/InputNumber';
import { Select } from 'core/components/ui/Select';

import { DIRECTIONS } from './literals';
import type { LocationProps, LocationValueProps, LocationValue } from './types';
import { ConvertDDToDMS } from './utils/convert-dd-to-dms';

const createOptions = (array: any) => array.map((value: any) => ({ label: value, value }));

const inputStyle = { width: '25%' };

function LocationWidget({ disabled, onChange, type, value }: LocationProps) {
  const [_value, setValue] = useState<LocationValueProps>();
  const [isLocation, setIsLocation] = useState(false);
  const [loading, setLoading] = useState(false);
  const maxDegrees: number = type === 'latitude' ? 90 : 180;

  useEffect(() => {
    if (!value || value.length === 0) {
      return;
    }

    setValue(value);
  }, [value]);

  useEffect(() => {
    if (window.navigator.geolocation) {
      const geolocationStatus = ({ state }: { state: string }) => {
        const permissions: Record<string, any> = {
          granted: () => {
            setIsLocation(true);
          },
          prompt: () => {
            setIsLocation(true);
          },
          denied: () => {
            setLoading(false);
            setIsLocation(false);
          },
        };
        permissions[state]();
      };

      navigator.permissions.query({ name: 'geolocation' }).then((result: any) => {
        geolocationStatus(result);
        // eslint-disable-next-line no-param-reassign
        result.addEventListener('change', () => geolocationStatus(result));
      });
    }
  }, []);

  const onValueChanged = (v: LocationValue | null, index: number) => {
    const newValue: any = _value ? [..._value] : [0, 0, 0, DIRECTIONS[type][0]];

    newValue[index] = v;
    onChange(newValue);
    setValue(newValue);
  };

  const setCurrentPosition = () => {
    setLoading(true);
    navigator.geolocation.getCurrentPosition((position: any) => {
      const values = ConvertDDToDMS(position.coords[type], type === 'latitude');

      setValue(values);
      onChange(values);
      setLoading(false);
    });
  };

  const tooltipTag = (
    <Button
      disabled={loading || disabled}
      icon={<AimOutlined />}
      loading={loading}
      onClick={setCurrentPosition}
      tooltip={{
        title: t('Tooltip.LocationWidget'),
        color: 'orange',
      }}
      type="primary"
    />
  );

  const confirmTag = (
    <Button
      disabled={loading || disabled}
      confirm={{
        title: t('Confirm.LocationWidget'),
        onConfirm: () => setCurrentPosition(),
      }}
      icon={<AimOutlined />}
      loading={loading}
      type="primary"
    />
  );

  const geolocationTag = _value?.length ? confirmTag : tooltipTag;

  return (
    <>
      <Input.Group compact>
        <InputNumber
          disabled={disabled}
          formatter={(v: LocationValue | undefined): string => (v ? `${String(v)}°` : '')}
          max={maxDegrees}
          min={0}
          onChange={(v: LocationValue | null) => onValueChanged(v, 0)}
          parser={(v: LocationValue | undefined): string => (v ? String(v).replace('°', '') : '')}
          style={inputStyle}
          value={_value ? _value[0] : 0}
        />
        <InputNumber
          disabled={disabled}
          formatter={(v: LocationValue | undefined) => (v ? `${String(v)}′` : '')}
          max={59}
          min={0}
          onChange={(v: LocationValue | null) => onValueChanged(v, 1)}
          parser={(v: LocationValue | undefined): string => (v ? String(v).replace('′', '') : '')}
          style={inputStyle}
          value={_value ? _value[1] : 0}
        />
        <InputNumber
          disabled={disabled}
          formatter={(v: LocationValue | undefined) => (v ? `${String(v)}″` : '')}
          max={59}
          min={0}
          onChange={(v: LocationValue | null) => onValueChanged(v, 2)}
          parser={(v: LocationValue | undefined): string => (v ? String(v).replace('″', '') : '')}
          style={inputStyle}
          value={_value ? _value[2] : 0}
        />
        <Select
          disabled={disabled}
          onChange={(v: LocationValue | null) => onValueChanged(v, 3)}
          options={createOptions(DIRECTIONS[type])}
          style={inputStyle}
          value={_value ? _value[3] : DIRECTIONS[type][0]}
        />
      </Input.Group>
      {isLocation ? geolocationTag : null}
    </>
  );
}

export { LocationWidget };
